import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { SeoService } from '@rpgc-ai/services/seo/seo.service';
import { getSeoLanguageCode, LANGUAGE_CODES } from '@rpgc-ai/services/translate/languages';

import type { PublicLandingLocale } from '@rpgc-ai/shared-interfaces/interfaces/public-landing.interface';

import { environment } from '@rpgc-ai/environments';

import { filter, tap } from 'rxjs/operators';

import type { IChatbotCanonicalSlugs } from '../resolvers/chatbot-seo.resolver';

@Injectable({ providedIn: 'root' })
export class HreflangService {
  readonly #document = inject(DOCUMENT);
  readonly #router = inject(Router);
  readonly #seo = inject(SeoService);

  constructor() {
    this.#router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        tap(({ urlAfterRedirects }) => this.#update(urlAfterRedirects)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  setLandingLinks(locale: PublicLandingLocale, slug: string, locales: PublicLandingLocale[]): void {
    const encodedSlug = encodeURIComponent(slug);
    const available = [...new Set(locales)];

    this.removeLandingLinks();
    this.#seo.setCanonicalPath(`/${locale}/landings/${encodedSlug}`);
    const alternatePaths = [];
    if (available.includes('en')) {
      alternatePaths.push({ hreflang: 'x-default', path: `/en/landings/${encodedSlug}` });
    }
    for (const language of available) {
      alternatePaths.push({ hreflang: getSeoLanguageCode(language), path: `/${language}/landings/${encodedSlug}` });
    }
    this.#seo.setAlternatePaths(alternatePaths);
  }

  removeLandingLinks(): void {
    this.#seo.removeCanonicalPath();
    this.#seo.removeAlternatePaths();
  }

  #update(url: string): void {
    // Strip query string, fragment, and trailing slashes
    const path = url.split('?')[0].split('#')[0].replace(/\/+$/, '');
    const segments = path.split('/').filter(Boolean);
    const lang = segments[0] ?? '';
    const currentRestPath = segments.length > 1 ? '/' + segments.slice(1).join('/') : '';
    const restPath = this.#canonicalRestPath() ?? currentRestPath;
    const domain = environment.APP_DOMAIN.replace(/^https?:\/\//, '');
    const origin = `https://${domain}`;
    const canonicalPath = lang ? `/${lang}${restPath}` : restPath;

    if (this.#landingOwnsSeo()) {
      return;
    }

    this.#updateCanonical(`${origin}${canonicalPath || '/'}`);
    this.#updateHreflang(origin, restPath);
  }

  #landingOwnsSeo(): boolean {
    let route = this.#router.routerState.snapshot.root;
    while (route) {
      if (route.data['isLandingSeoOwner'] === true) {
        return true;
      }
      if (!route.firstChild) {
        break;
      }
      route = route.firstChild;
    }
    return false;
  }

  /**
   * Карточка персонажа доступна по короткой и по канонической форме маршрута; обе обязаны
   * указывать на канонический адрес, иначе один персонаж читается поисковиком как две страницы.
   * Пару слагов кладёт в `data` резолвер маршрута, а роутер к моменту NavigationEnd уже её резолвил.
   */
  #canonicalRestPath(): string | null {
    let route = this.#router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const slugs = route.data['seo'] as Partial<IChatbotCanonicalSlugs> | undefined;
    const { characterSlug, authorSlug } = slugs ?? {};
    if (!characterSlug || !authorSlug) {
      return null;
    }

    return `/view-chatbot/${authorSlug}/${characterSlug}`;
  }

  #updateCanonical(href: string): void {
    let link = this.#document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.#document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.#document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  #updateHreflang(origin: string, restPath: string): void {
    this.#document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

    this.#addAlternate(origin, 'x-default', `/en${restPath}`);

    for (const lang of LANGUAGE_CODES) {
      this.#addAlternate(origin, getSeoLanguageCode(lang), `/${lang}${restPath}`);
    }
  }

  #addAlternate(origin: string, hreflang: string, path: string): void {
    const link = this.#document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', `${origin}${path}`);
    this.#document.head.appendChild(link);
  }
}
